import {
    inject,
    runInInjectionContext,
    type Injector,
    type Signal,
} from '@angular/core';
import {
    assertInjector,
    type QueryStatus,
    type ResolvedProblemDetails,
} from '@quipex/core/utils';
import type { Observable } from 'rxjs';
import { filter } from 'rxjs';
import type { CurrentOrganisation } from './models';
import { UserStore } from './user.store';

export interface InjectOrg {
    /** Returns organization data or null */
    (): CurrentOrganisation | null;
    /** Observable of organization data */
    $: Observable<CurrentOrganisation | null>;
    /** Observable of organization data filtered to non-null values (for route-guarded components where org must be loaded) */
    required$: Observable<CurrentOrganisation>;

    // Query-aligned status signals
    status: Signal<QueryStatus>;
    isIdle: Signal<boolean>;
    isLoading: Signal<boolean>;
    isSuccess: Signal<boolean>;
    isError: Signal<boolean>;
    isFetched: Signal<boolean>;
    error: Signal<ResolvedProblemDetails | null>;

    /** Asserts organization is loaded - throws if not */
    required: () => CurrentOrganisation;
}

export function injectOrg(injector?: Injector) {
    const resolvedInjector = assertInjector(injectOrg, injector);

    const userStore = runInInjectionContext(resolvedInjector, () => {
        return inject(UserStore);
    });

    const org = userStore.org; // Already a signal

    const required = () => {
        const o = org();
        if (!o) {
            throw new TypeError(
                'Organization is either not loaded or not found'
            );
        }
        return o;
    };

    const fn = (() => org()) as InjectOrg;
    fn.$ = userStore.org$;
    fn.required$ = userStore.org$.pipe(
        filter((o): o is CurrentOrganisation => o !== null)
    );
    // Org state is now derived from meQuery.
    fn.status = userStore.meQuery.status;
    fn.isIdle = userStore.meQuery.isIdle;
    fn.isLoading = userStore.meQuery.isLoading;
    fn.isSuccess = userStore.meQuery.isSuccess;
    fn.isError = userStore.meQuery.isError;
    fn.isFetched = userStore.meQuery.isFetched;
    fn.error = userStore.meQuery.error;
    fn.required = required;
    return fn;
}
